import type { PointerEvent as ReactPointerEvent, SyntheticEvent } from "react";
import { useMemo, useRef } from "react";
import type { RowData } from "@tanstack/react-table";
import type { DataGridPin, DataGridTable } from "../columns/types";
import type { GridController } from "../focus/gridController";
import { getDisplayColumns } from "../focus/navigation";
import { createColumnResizer } from "./columnResizer";
import {
    findPinEdgeBorder,
    findResizeBorder,
    type PinEdge,
    type ResizeBorder,
} from "./findResizeBorder";

const HOVER_ATTRIBUTE = "data-dt-border-resize";
const PIN_EDGE_HOVER_ATTRIBUTE = "data-dt-pin-edge-hover";

const hasColumnLines = (controller: GridController) => {
    const lines = controller.root?.dataset.gridLines;
    return lines === "both" || lines === "columns";
};

/**
 * The pin borders on screen: a side draws one only while columns scroll
 * under it (`updateOverflowEdges`) and its pins stay stuck (`ColumnSizeVars`).
 * The border's column is the header cell marked `data-pin-edge` (`getPinEdge`).
 */
const findShownPinEdges = <TData extends RowData>({
    table,
    controller,
    viewport,
}: {
    table: DataGridTable<TData>;
    controller: GridController;
    viewport: HTMLElement;
}): PinEdge[] => {
    const unstuckSides = controller.root?.dataset.unstuckPins ?? "";
    return (["start", "end"] as const).flatMap((side) => {
        if (
            !viewport.hasAttribute(`data-overflow-${side}`) ||
            unstuckSides.includes(side)
        ) {
            return [];
        }
        const header = viewport.querySelector<HTMLElement>(
            `[role='columnheader'][data-pin-edge="${side}"]`,
        );
        const columnId = header?.dataset.columnId;
        const column = columnId ? table.getColumn(columnId) : undefined;
        if (!header || !column) {
            return [];
        }
        const rect = header.getBoundingClientRect();
        return [
            {
                side,
                columnId: column.id,
                x: side === "start" ? rect.right : rect.left,
                canResize: column.getCanResize(),
            },
        ];
    });
};

/** A pin border under the pointer, on a row or the header; `null` elsewhere. */
const findPinBorder = <TData extends RowData>({
    table,
    controller,
    event,
}: {
    table: DataGridTable<TData>;
    controller: GridController;
    event: ReactPointerEvent<HTMLElement>;
}) =>
    event.target instanceof Element && event.target.closest("[role='row']")
        ? findPinEdgeBorder({
              pointerX: event.clientX,
              edges: findShownPinEdges({
                  table,
                  controller,
                  viewport: event.currentTarget,
              }),
          })
        : null;

const findCell = (event: ReactPointerEvent) =>
    event.target instanceof Element
        ? event.target.closest<HTMLElement>("[role='gridcell'][data-column-id]")
        : null;

const findBorder = <TData extends RowData>({
    table,
    event,
}: {
    table: DataGridTable<TData>;
    event: ReactPointerEvent;
}): ResizeBorder | null => {
    const cell = findCell(event);
    const columnId = cell?.dataset.columnId;
    if (!cell || !columnId) {
        return null;
    }
    const displayColumns = getDisplayColumns(table);
    const index = displayColumns.findIndex((column) => column.id === columnId);
    const nextId = displayColumns[index + 1]?.id;
    const nextCell = nextId
        ? cell.parentElement?.querySelector<HTMLElement>(
              `[data-column-id="${CSS.escape(nextId)}"]`,
          )
        : null;
    const rect = cell.getBoundingClientRect();
    if (rect.width === 0) {
        return null;
    }
    return findResizeBorder({
        pointerX: event.clientX,
        cellLeft: rect.left,
        cellRight: rect.right,
        columnId,
        columns: displayColumns.map((column) => ({
            id: column.id,
            pin: column.getIsPinned(),
            canResize: column.getCanResize(),
        })),
        isNextAdjacent:
            nextCell !== null &&
            nextCell !== undefined &&
            Math.abs(nextCell.getBoundingClientRect().left - rect.right) < 1,
    });
};

/** Swallows the click that ends a border drag, so the cell under it isn't selected. */
const swallowNextClick = (viewport: HTMLElement) => {
    const swallow = (event: MouseEvent) => event.stopPropagation();
    viewport.addEventListener("click", swallow, { capture: true, once: true });
    setTimeout(() => viewport.removeEventListener("click", swallow, true));
};

/**
 * Cell borders that resize their column like the header handles: the cursor
 * turns `col-resize` near one and a drag moves it. A shown pin border
 * (`findShownPinEdges`) works this way along its whole length, header
 * included, and marks the viewport so the full border highlights; with
 * column grid lines on, every cell's left and right borders do too, marking
 * the one cell under the pointer so only it restyles. Viewport-level
 * handlers, so rows stay untouched. A scroll or key press can move a border
 * out from under a still pointer, so either clears the marks until the next
 * pointer move. The marked cell lives in a ref: the
 * handlers rebuild whenever the table changes identity, and must still
 * unmark it.
 */
export const useBorderResize = <TData extends RowData>({
    table,
    controller,
}: {
    table: DataGridTable<TData>;
    controller: GridController;
}) => {
    const hoveredCellRef = useRef<HTMLElement | null>(null);
    return useMemo(() => {
        const markHovered = (cell: HTMLElement | null) => {
            if (cell === hoveredCellRef.current) {
                return;
            }
            hoveredCellRef.current?.removeAttribute(HOVER_ATTRIBUTE);
            cell?.setAttribute(HOVER_ATTRIBUTE, "");
            hoveredCellRef.current = cell;
        };
        const markPinEdge = ({
            viewport,
            side,
        }: {
            viewport: HTMLElement;
            side: DataGridPin | null;
        }) => {
            if (side) {
                viewport.setAttribute(PIN_EDGE_HOVER_ATTRIBUTE, side);
            } else {
                viewport.removeAttribute(PIN_EDGE_HOVER_ATTRIBUTE);
            }
        };
        const findAnyBorder = (event: ReactPointerEvent<HTMLElement>) =>
            findPinBorder({ table, controller, event }) ??
            (hasColumnLines(controller) ? findBorder({ table, event }) : null);
        const clearMarks = (event: SyntheticEvent<HTMLElement>) => {
            markHovered(null);
            markPinEdge({ viewport: event.currentTarget, side: null });
        };
        return {
            onPointerLeave: clearMarks,
            onScroll: clearMarks,
            onKeyDownCapture: clearMarks,
            onPointerMove: (event: ReactPointerEvent<HTMLElement>) => {
                if (event.buttons !== 0) {
                    markHovered(null);
                    return;
                }
                const pinBorder = findPinBorder({ table, controller, event });
                markPinEdge({
                    viewport: event.currentTarget,
                    side: pinBorder?.side ?? null,
                });
                const isOverBorder =
                    !pinBorder &&
                    hasColumnLines(controller) &&
                    findBorder({ table, event }) !== null;
                markHovered(isOverBorder ? findCell(event) : null);
            },
            onPointerDown: (event: ReactPointerEvent<HTMLElement>) => {
                if (event.button !== 0) {
                    return;
                }
                const border = findAnyBorder(event);
                const column = border && table.getColumn(border.columnId);
                if (!border || !column) {
                    return;
                }
                event.preventDefault();
                event.stopPropagation();
                const viewport = event.currentTarget;
                createColumnResizer({
                    column,
                    controller,
                    edge: border.edge,
                }).startDrag({
                    startX: event.clientX,
                    onEnd: ({ didMove }) => {
                        if (didMove) {
                            swallowNextClick(viewport);
                        }
                    },
                });
            },
        };
    }, [table, controller]);
};
